import { useEffect, useState } from 'react';

import {
    Box, Button, FormControl, FormLabel, Input, Select, Stack, Text, useToast,
} from '@chakra-ui/react';

import { supabase } from '../lib/supabase';

export default function CheckFraud() {
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [banks, setBanks] = useState<
    { bank_code: string; bank_name: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    isFraudulent: boolean;
    reports: number;
    lastReported: string | null;
  } | null>(null);
  const toast = useToast();

  useEffect(() => {
    async function fetchBanks() {
      const { data, error } = await supabase
        .from("bank_list")
        .select("bank_code, bank_name");
      if (error) {
        console.error("Error fetching banks:", error);
      } else {
        setBanks(data || []);
      }
    }
    fetchBanks();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.rpc("check_fraud", {
        p_account_number: accountNumber,
        p_account_type: accountType,
      });

      if (error) throw error;

      const res = Array.isArray(data) ? data[0] : data;
      if (res) {
        setResult({
          isFraudulent: res.is_fraudulent,
          reports: res.reports,
          lastReported: res.last_reported,
        });
      } else {
        setResult(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to check account",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={8} p={6}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <FormControl isRequired>
            <FormLabel>Account Number</FormLabel>
            <Input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="Enter account number, e-wallet ID, or crypto address"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Account Type</FormLabel>
            <Select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              placeholder="Select account type"
            >
              <option value="bank">Bank Account</option>
              <option value="ewallet">E-Wallet</option>
              <option value="crypto">Crypto Address</option>
            </Select>
          </FormControl>

          {accountType === "bank" ? (
            <FormControl isRequired>
              <FormLabel>Bank</FormLabel>
              <Select
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value)}
                placeholder="Select a bank"
              >
                {banks.map((bank) => (
                  <option key={bank.bank_code} value={bank.bank_code}>
                    {bank.bank_name}
                  </option>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl isRequired>
              <FormLabel>Institution Name</FormLabel>
              <Input
                type="text"
                value={institutionName}
                onChange={(e) => setInstitutionName(e.target.value)}
                placeholder="Enter institution name"
              />
            </FormControl>
          )}

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
            width="full"
          >
            Check Account
          </Button>
        </Stack>
      </form>

      {result && (
        <Box mt={8} p={6} borderWidth={1} borderRadius="lg">
          <Stack spacing={4}>
            <Text
              fontSize="xl"
              fontWeight="bold"
              color={result.isFraudulent ? "red.500" : "green.500"}
            >
              {result.isFraudulent
                ? "⚠️ Similar transactions have been reported as suspicious by the community"
                : "✅ No fraud reports found for this account"}
            </Text>
            <Text>
              Number of reports: <strong>{result.reports}</strong>
            </Text>
            {result.lastReported && (
              <Text>
                Last reported: <strong>{result.lastReported}</strong>
              </Text>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
